import { useEffect, useRef } from 'react';
import { focusElement } from 'platform/utilities/ui';
import { FIELD_NAMES } from '../constants';
import { getEditButtonId } from '../util/id-factory';
import {
  FOCUS_DELAY,
  focusCancelButton,
  focusErrorCloseButton,
  focusFirstFormElement,
} from '../util/profile-information-field/editViewFocus';
import {
  EDIT_VIEW_UPDATE_EFFECTS,
  getEditViewUpdateEffects,
} from '../util/profile-information-field/getEditViewUpdateEffects';

/**
 * The edit view's mount, update and unmount side effects: seeding the form
 * with the field's current values, focus management, and the transaction
 * request cleanup.
 *
 * The update decisions live in getEditViewUpdateEffects; this hook only
 * executes them. Transaction polling is separate — see useTransactionPolling.
 */
export const useEditViewEffects = ({
  field,
  fieldName,
  forceEditView,
  formSchema,
  shouldFocusCancelButton,
  transaction,
  transactionRequest,
  uiSchema,
  editFormRef,
  getInitialFormValues,
  onCancelButtonFocused,
  onClearTransactionRequest,
  onOpenAddressValidation,
  onUpdateFormData,
}) => {
  const prevRef = useRef(null);
  // PORT NOTE (bug fix): the class never cleared these timers, so a view that
  // unmounted inside the 100ms window still ran its focus callback — including
  // onCancelButtonFocused, which sets state on the field controller.
  const timeoutsRef = useRef([]);

  // Latest values, read at effect time so they never need to be dependencies.
  // The unmount cleanup in particular has to see the current transaction
  // request rather than the one that existed at mount.
  const latestRef = useRef(null);
  latestRef.current = {
    fieldName,
    forceEditView,
    formSchema,
    getInitialFormValues,
    onCancelButtonFocused,
    onClearTransactionRequest,
    onOpenAddressValidation,
    onUpdateFormData,
    transactionRequest,
    uiSchema,
  };

  const schedule = callback => {
    timeoutsRef.current.push(setTimeout(callback, FOCUS_DELAY));
  };

  const scheduleFocusFirstFormElement = () => {
    // Showing the edit view on its own page, so let the app handle focus
    if (latestRef.current.forceEditView || !editFormRef.current) {
      return;
    }

    schedule(() => focusFirstFormElement(editFormRef.current));
  };

  // Mount: seed the form with the field's current values, then focus it.
  useEffect(() => {
    const {
      formSchema: mountFormSchema,
      uiSchema: mountUiSchema,
      getInitialFormValues: getValues,
      onUpdateFormData: updateFormData,
    } = latestRef.current;

    updateFormData(getValues(), mountFormSchema, mountUiSchema);
    scheduleFocusFirstFormElement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PORT NOTE: there is no dependency array. componentDidUpdate ran after every
  // re-render regardless of which props changed, and an effect with no deps is
  // the faithful equivalent — the previous-value comparison inside decides
  // whether anything actually happens.
  useEffect(() => {
    const prev = prevRef.current;
    const next = {
      field,
      shouldFocusCancelButton,
      transaction,
      transactionRequest,
    };

    prevRef.current = next;

    // PORT NOTE: componentDidUpdate never ran for the initial render.
    if (!prev) {
      return;
    }

    const effects = getEditViewUpdateEffects({ prev, next });

    effects.forEach(effect => {
      switch (effect.type) {
        case EDIT_VIEW_UPDATE_EFFECTS.FOCUS_CANCEL_BUTTON:
          // va-button is a web component, so the native button inside it only
          // exists once the shadow DOM has rendered.
          schedule(() => {
            focusCancelButton(editFormRef.current);
            latestRef.current.onCancelButtonFocused?.();
          });
          break;

        case EDIT_VIEW_UPDATE_EFFECTS.FOCUS_FIRST_FORM_ELEMENT:
          scheduleFocusFirstFormElement();
          break;

        case EDIT_VIEW_UPDATE_EFFECTS.OPEN_ADDRESS_VALIDATION_MODAL:
          latestRef.current.onOpenAddressValidation();
          break;

        case EDIT_VIEW_UPDATE_EFFECTS.FOCUS_ERROR_CLOSE_BUTTON:
          focusErrorCloseButton();
          break;

        /* c8 ignore next 2 */
        default:
          break;
      }
    });
  });

  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);

      const {
        fieldName: currentFieldName,
        transactionRequest: currentTransactionRequest,
        onClearTransactionRequest: clearRequest,
      } = latestRef.current;

      // Errors returned directly from the API request (as opposed to through a
      // transaction lookup) are displayed in this modal, rather than on the
      // page. Once the modal is closed, reset the state for the next time the
      // modal is opened by removing any existing transaction request from the
      // store.
      //
      // The class paired this with an `else` that dismissed the MHV email
      // confirmation alert. That half lives in useTransactionPolling, where the
      // same "only when the request did not error" guard keeps the two
      // mutually exclusive.
      if (currentTransactionRequest?.error) {
        clearRequest();
      }

      if (currentFieldName === FIELD_NAMES.RESIDENTIAL_ADDRESS) {
        focusElement(`#${getEditButtonId(currentFieldName)}`);
      }
    };
  }, []);
};
